import React, { useState, useEffect, useContext, useRef } from 'react';
import { useFormContext, Controller, get, useWatch } from 'react-hook-form';
import { ChevronDown, ChevronUp } from 'lucide-react';
import CurrencyInput from '../../../../../../components/CurrencyInput';

import { OrderSchemaType, ItemPath } from '../../order.schema';
import { computeDelivery, buildHolidayMessage, formatDeliveryDate } from '../../utils/businessDays';
import { FeedbackContext } from '../../../../../../contexts/FeedbackContext';
import { useItemSubtotalCalculator } from '../../Hooks/useItemSubtotalCalculator';
import {
  CollapsibleHeader,
  CircularCollapseIcon,
  FormGroup,
  Input,
  Label,
  Separator,
  SubTitle
} from '../../style';
import { OrderItemType } from '../../types';
import { PriceScroll, PriceGrid, PriceColHead, PriceValue, PriceCaption, SpacedFormGrid } from './PricesSection.style';


interface PricesSectionProps {
  itemPath: ItemPath;
  quantity: number;
}

const PricesSection: React.FC<PricesSectionProps> = ({ itemPath, quantity }) => {
  const [isPriceOpen, setIsPriceOpen] = useState(true);

  const { control, register, watch, setValue, formState: { errors } } = useFormContext<OrderSchemaType>();

  const itemType = useWatch({ control, name: `${itemPath}.itemType` });

  useItemSubtotalCalculator({ itemPath, itemType: itemType as OrderItemType, quantity });

  const priceDetailsPath = `${itemPath}.priceDetails` as const;

  const itemPriceWatch = useWatch({ control, name: `${itemPath}.itemPrice` as any });
  const sellingPriceWatch = useWatch({ control, name: `${priceDetailsPath}.sellingPrice` as any });
  const quantityPiecesWatch = useWatch({ control, name: `${itemPath}.quantityPieces` as any });

  const numItemPrice = Number(itemPriceWatch) || 0;
  const numSellingPrice = Number(sellingPriceWatch) || 0;
  const numPieces = Number(quantityPiecesWatch) || 0;
  const itemSubtotalWatch = useWatch({ control, name: `${priceDetailsPath}.itemSubtotal` as any });
  const numSubtotal = Number(itemSubtotalWatch) || 0;
  const effectivePrice = numSellingPrice > 0 ? numSellingPrice : numItemPrice;

  const orderDateWatch = useWatch({ control, name: 'orderDate' as any });
  const businessDaysWatch = useWatch({ control, name: `${itemPath}.deliveryBusinessDays` as any });
  const [deliveryPreview, setDeliveryPreview] = useState<{ date: string; notice: string | null }>({ date: '', notice: null });
  const { setFeedback } = useContext(FeedbackContext);
  const previewFailedRef = useRef(false);

  useEffect(() => {
    let active = true;
    const days = Number(businessDaysWatch);
    if (!days || isNaN(days) || days <= 0) {
      setDeliveryPreview({ date: '', notice: null });
      return;
    }
    const notifyFailure = () => {
      setDeliveryPreview({ date: '', notice: null });
      if (previewFailedRef.current) return;
      previewFailedRef.current = true;
      setFeedback({ message: 'Não foi possível calcular a previsão de entrega do item.', isOpen: true, statusOk: false });
    };
    const timer = setTimeout(() => {
      computeDelivery(orderDateWatch as string | undefined, days)
        .then(computation => {
          if (!active) return;
          if (computation.failed || !computation.date) {
            notifyFailure();
            return;
          }
          previewFailedRef.current = false;
          setDeliveryPreview({ date: computation.date, notice: buildHolidayMessage(computation) });
        })
        .catch(() => { if (active) notifyFailure(); });
    }, 400);
    return () => { active = false; clearTimeout(timer); };
  }, [businessDaysWatch, orderDateWatch, setFeedback]);

  const round2 = (v: number) => parseFloat(v.toFixed(2));

  const derivedDiscount = numItemPrice > 0 && numSellingPrice > 0
    ? round2(((numItemPrice - numSellingPrice) / numItemPrice) * 100)
    : '';
  const isAjuste = typeof derivedDiscount === 'number' && derivedDiscount < 0;
  const displayDiscount = typeof derivedDiscount === 'number' && isAjuste
    ? Math.abs(derivedDiscount)
    : derivedDiscount;
  const displaySubtotal = numSellingPrice > 0 ? numSellingPrice : numSubtotal;
  const derivedUnitPrice = numPieces > 0 && effectivePrice > 0
    ? round2(effectivePrice / numPieces)
    : 0;
  const itemUnitName = useWatch({ control, name: `${itemPath}.unitOfMeasurementName` as any }) as string | undefined;
  const derivedUnitPriceInUnit = quantity > 0 && effectivePrice > 0
    ? round2(effectivePrice / quantity)
    : 0;

  const writeSellingPrice = (value: number | undefined) => {
    setValue(`${priceDetailsPath}.sellingPrice` as any, value, { shouldDirty: true, shouldValidate: false });
  };

  const handleDiscountChange = (raw: string) => {
    const d = Number(String(raw).replace(',', '.'));
    if (raw === '' || isNaN(d)) {
      writeSellingPrice(undefined);
      return;
    }
    if (numItemPrice > 0) {
      const signed = isAjuste ? -Math.abs(d) : d;
      writeSellingPrice(round2(numItemPrice * (1 - signed / 100)));
    }
  };

  const handleUnitPriceChange = (value: number | undefined) => {
    if (numPieces > 0 && value !== undefined && value > 0) {
      writeSellingPrice(round2(value * numPieces));
    }
  };

  const [pricePerKgMinRaw] = watch([
    `${priceDetailsPath}.pricePerKgMin`,
  ] as const) as [number | undefined];

  const pricePerKgMin: number | undefined = typeof pricePerKgMinRaw === 'number' ? pricePerKgMinRaw : undefined;

  return (
    <>
      <Separator />
      <CollapsibleHeader onClick={() => setIsPriceOpen(prev => !prev)}>
        <SubTitle>Preço</SubTitle>
        <CircularCollapseIcon onClick={(e) => { e.stopPropagation(); setIsPriceOpen(prev => !prev); }}>
          {isPriceOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </CircularCollapseIcon>
      </CollapsibleHeader>

      {isPriceOpen && (
        <>
          {/* Mesma gramática visual do fechamento: labels-coluna + linha única alinhada. */}
          <PriceScroll>
            <PriceColHead>
              <span>Cadastrado</span>
              <span>Preço de Venda</span>
              <span>{isAjuste ? 'Ajuste (%)' : 'Desconto (%)'}</span>
              <span>Unitário (pç)</span>
              <span>Unitário{itemUnitName ? ` (${itemUnitName})` : ' (un)'}</span>
              <span>Subtotal</span>
            </PriceColHead>
            <PriceGrid>
              <PriceValue>
                {numItemPrice > 0 ? numItemPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
              </PriceValue>
              <Controller
                name={`${priceDetailsPath}.sellingPrice` as const}
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id={`${priceDetailsPath}.sellingPrice`}
                    name={`${priceDetailsPath}.sellingPrice`}
                    value={field.value || ''}
                    onValueChange={(value, name, values) => {
                      field.onChange(values?.float || undefined);
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, `${priceDetailsPath}.sellingPrice`)}
                  />
                )}
              />
              <Input
                id={`${priceDetailsPath}.discount`}
                type="number"
                step="any"
                value={displayDiscount}
                onChange={(e) => handleDiscountChange(e.target.value)}
                placeholder="%"
                disabled={numItemPrice <= 0}
              />
              <CurrencyInput
                id={`${priceDetailsPath}.unitPriceEntry`}
                name={`${priceDetailsPath}.unitPriceEntry`}
                value={derivedUnitPrice}
                onValueChange={(value, name, values) => {
                  handleUnitPriceChange(values?.float ?? undefined);
                }}
                placeholder={numPieces > 0 ? 'R$ 0,00' : 'Sem qtd de peças'}
              />
              <PriceValue>
                {derivedUnitPriceInUnit > 0 ? derivedUnitPriceInUnit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
              </PriceValue>
              <PriceValue>
                {displaySubtotal > 0 ? displaySubtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
              </PriceValue>
            </PriceGrid>
          </PriceScroll>
          <PriceCaption>
            Desconto e unitário recalculam o Preço de Venda · vazio: vale o Cadastrado.
          </PriceCaption>

          <SpacedFormGrid columns={3}>
            <FormGroup>
              <Label htmlFor={`${itemPath}.deliveryBusinessDays`}>Prazo de entrega (dias úteis)</Label>
              <Input
                id={`${itemPath}.deliveryBusinessDays`}
                type="number"
                min={0}
                {...register(`${itemPath}.deliveryBusinessDays` as const, {
                  setValueAs: (value) => value === '' || value === null ? undefined : Number(value),
                })}
                placeholder="15"
                hasError={!!get(errors, `${itemPath}.deliveryBusinessDays`)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Previsão de entrega</Label>
              <PriceValue>{deliveryPreview.date ? formatDeliveryDate(deliveryPreview.date) : '—'}</PriceValue>
            </FormGroup>
          </SpacedFormGrid>
          {deliveryPreview.notice && <PriceCaption>{deliveryPreview.notice}</PriceCaption>}
          <Separator />
        </>
      )}
    </>
  );
};

export default PricesSection;